// Dialectes CSV des plateformes tierces.
//
// L'import n'acceptait que nos propres noms de colonnes (date, symbol,
// quantity...). Un export TopstepX, pourtant complet, était rejeté avec
// "En-têtes manquants" — l'utilisateur devait renommer ses colonnes à la main.
//
// Un dialecte décrit comment traduire les en-têtes d'une plateforme vers le
// format natif. La détection est volontairement stricte : on exige les colonnes
// signature du format, pour ne jamais réinterpréter de travers un CSV qui
// ressemblerait vaguement à un export connu.

import { stripFuturesExpiration } from "../shared/utils";
import { matchExecutionsFifo, type QuantowerExecution } from "./quantowerFifo";
import { frenchNumber, quantowerDate } from "./quantowerCsv";

/** Ligne CSV brute, indexée par en-tête normalisé (minuscules, sans espaces). */
export type RawRow = Record<string, string>;

/** Ligne au format natif attendu par csvRowSchema. */
export interface NativeRow {
  date: string;
  symbol: string;
  type: string;
  status: string;
  entryPrice: string;
  exitPrice: string;
  exitDate: string;
  quantity: string;
  pnl: string;
  commission: string;
  swaps: string;
  notes: string;
}

export interface CsvDialect {
  id: string;
  label: string;
  /** En-têtes qui doivent TOUS être présents pour reconnaître le format. */
  signature: string[];
  /**
   * Traduction ligne à ligne, quand une ligne du fichier vaut un trade.
   * C'est le cas de la plupart des exports (TopstepX...).
   */
  map?: (row: RawRow) => NativeRow;
  /**
   * Traduction du fichier entier, quand un trade se déduit de PLUSIEURS lignes.
   * Quantower exporte des exécutions : la reconstitution des allers-retours
   * demande de voir toutes les lignes, triées chronologiquement.
   *
   * Renvoie aussi les erreurs de niveau fichier (fermeture sans position
   * ouverte), que le mapping ligne à ligne ne peut pas exprimer.
   */
  mapFile?: (rows: RawRow[]) => {
    rows: NativeRow[];
    errors: Array<{ row: number; error: string }>;
  };
}

/** Normalise un en-tête pour la comparaison : minuscules, sans espaces. */
export function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\s+/g, "");
}

function num(value: string | undefined): number {
  const parsed = parseFloat((value ?? "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Symbole lisible depuis un nom de contrat TopstepX.
 * "CON.F.US.MNQ.Z25" -> "MNQ". Format inattendu : on renvoie la valeur brute
 * plutôt que d'inventer un symbole.
 */
function symbolFromContract(contractName: string): string {
  const raw = (contractName ?? "").trim();
  const parts = raw.split(".");
  if (parts.length >= 5 && parts[0].toUpperCase() === "CON") {
    return parts[3].toUpperCase();
  }
  return raw;
}

/** "Long"/"Buy" -> "long", "Short"/"Sell" -> "short". */
function normalizeSide(value: string): string {
  const v = (value ?? "").trim().toLowerCase();
  if (v === "buy" || v === "long") return "long";
  if (v === "sell" || v === "short") return "short";
  return v;
}

/**
 * Frais TopstepX -> convention du projet.
 *
 * TopstepX exporte `fees` et `commissions` séparément, en montants POSITIFS.
 * Le projet stocke la commission NÉGATIVE (netPnl = pnl + commission + swaps,
 * cf. tradovateTradeMapper). On cumule puis on force le signe négatif — sans
 * ré-inverser une valeur déjà négative, qui serait sinon comptée comme un gain.
 */
function feesToCommission(row: RawRow): string {
  const total = num(row.fees) + num(row.commissions);
  if (total === 0) return "0";
  return String(-Math.abs(total));
}

const topstepx: CsvDialect = {
  id: "topstepx",
  label: "TopstepX",
  // contractname + enteredat + entryprice : sans elles on ne sait ni quoi, ni
  // quand, ni à quel prix — un fichier tronqué ne doit pas passer.
  signature: ["contractname", "enteredat", "entryprice", "pnl", "size"],
  map: (row) => {
    const exitedAt = (row.exitedat ?? "").trim();
    return {
      // enteredat est l'horodatage complet ; tradeday (date seule) sert de
      // repli si la colonne est vide.
      date: (row.enteredat ?? "").trim() || (row.tradeday ?? "").trim(),
      symbol: symbolFromContract(row.contractname),
      type: normalizeSide(row.type),
      status: exitedAt ? "closed" : "open",
      entryPrice: (row.entryprice ?? "").trim(),
      exitPrice: (row.exitprice ?? "").trim(),
      exitDate: exitedAt,
      quantity: (row.size ?? "").trim(),
      pnl: (row.pnl ?? "").trim(),
      commission: feesToCommission(row),
      swaps: "",
      notes: "",
    };
  },
};

/**
 * Quantower exporte des ORDRES EXÉCUTÉS, pas des trades : une ligne ne porte
 * jamais à la fois l'entrée et la sortie, et le `Gross P/L` n'apparaît que sur
 * les lignes qui ferment une position. On reconstitue les allers-retours en
 * FIFO (cf. quantowerFifo.ts) plutôt que de produire deux fois trop de trades.
 */
const quantower: CsvDialect = {
  id: "quantower",
  label: "Quantower",
  // side + grossp/l + fee + date/time est propre à Quantower : aucune collision
  // possible avec TopstepX (contractname/enteredat) ni avec le format natif.
  // normalizeHeader retire les espaces mais conserve '/' : "Date/Time" -> "date/time".
  signature: [
    "account",
    "date/time",
    "symbol",
    "side",
    "quantity",
    "price",
    "grossp/l",
    "fee",
  ],
  mapFile: (rows) => {
    const executions: QuantowerExecution[] = rows.map((row) => {
      // `Side` fait foi pour le sens. Certains exports signent aussi la
      // quantité (-1 pour un Sell), d'autres non : se fier au seul signe
      // transformerait tout un fichier non signé en achats, sans jamais rien
      // apparier — positions inventées et P&L jeté, sans la moindre erreur.
      const isSell = normalizeSide(row.side ?? "") === "short";
      const magnitude = Math.abs(frenchNumber(row.quantity));

      return {
        time: quantowerDate(row["date/time"] ?? ""),
        // MNQU6 -> MNQ, aligné sur TopstepX et Tradovate.
        symbol: stripFuturesExpiration((row.symbol ?? "").trim()),
        quantity: isSell ? -magnitude : magnitude,
        price: frenchNumber(row.price),
        fee: frenchNumber(row.fee),
        grossPnl: frenchNumber(row["grossp/l"]),
      };
    });

    const { trades, errors } = matchExecutionsFifo(executions);

    return {
      rows: trades.map((trade) => ({
        date: trade.entryDate,
        symbol: trade.symbol,
        type: trade.type,
        status: trade.status,
        entryPrice: String(trade.entryPrice),
        exitPrice: trade.exitPrice === null ? "" : String(trade.exitPrice),
        exitDate: trade.exitDate ?? "",
        quantity: String(trade.quantity),
        pnl: trade.pnl === null ? "" : String(trade.pnl),
        commission: String(trade.commission),
        swaps: "",
        notes: "",
      })),
      errors,
    };
  },
};

/**
 * Export Tradovate (Performance > Trades).
 *
 * L'app savait déjà lire ce format, mais uniquement via l'onglet « Coller
 * depuis Tradovate ». Un utilisateur qui passait par « Importer un CSV » avec
 * le même contenu se voyait répondre « En-têtes manquants ». Le dialecte lève
 * cette incohérence : le bouton d'import accepte désormais tous les formats.
 *
 * Les règles de lecture sont celles de tradovateCSVParser, à dessein : un même
 * fichier doit donner le même résultat par les deux chemins.
 */
const tradovate: CsvDialect = {
  id: "tradovate",
  label: "Tradovate",
  // buyfillid/sellfillid sont propres à Tradovate : aucune collision avec
  // TopstepX (contractname/enteredat) ni avec le format natif.
  signature: ["symbol", "qty", "buyprice", "sellprice", "pnl", "boughttimestamp"],
  map: (row) => {
    // Tradovate date en MM/DD/YYYY HH:mm:ss, parfois entre guillemets.
    const toIso = (raw: string): string => {
      const clean = (raw ?? "").replace(/"/g, "").trim();
      const [datePart, timePart] = clean.split(" ");
      if (!datePart || !timePart) return "";
      const [month, day, year] = datePart.split("/");
      if (!month || !day || !year) return "";
      return `${year}-${month}-${day}T${timePart}`;
    };

    // Le pnl arrive sous les formes "1,220.50" ou "$(310.00)" pour une perte.
    const toNumber = (raw: string): number => {
      const r = (raw ?? "").replace(/"/g, "").trim();
      if (/^\$\([\d.,]+\)$/.test(r)) return -parseFloat(r.replace(/[$(),]/g, ""));
      return parseFloat(r.replace(/[$,()]/g, ""));
    };

    const boughtAt = toIso(row.boughttimestamp ?? "");
    const soldAt = toIso(row.soldtimestamp ?? "");

    // Le sens ne figure dans aucune colonne : il se déduit de l'ordre des
    // horodatages. Racheté après avoir vendu = short, et le prix d'entrée est
    // alors le prix de VENTE. Inverser les deux ferait un PnL de signe opposé.
    const isShort = !!boughtAt && !!soldAt && new Date(boughtAt) > new Date(soldAt);

    const buyPrice = (row.buyprice ?? "").replace(/"/g, "").trim();
    const sellPrice = (row.sellprice ?? "").replace(/"/g, "").trim();
    const pnl = toNumber(row.pnl ?? "");

    return {
      date: isShort ? soldAt : boughtAt,
      symbol: stripFuturesExpiration((row.symbol ?? "").trim()),
      type: isShort ? "short" : "long",
      status: "closed",
      entryPrice: isShort ? sellPrice : buyPrice,
      exitPrice: isShort ? buyPrice : sellPrice,
      exitDate: isShort ? boughtAt : soldAt,
      quantity: (row.qty ?? "").trim(),
      pnl: Number.isFinite(pnl) ? String(pnl) : "",
      // L'export Tradovate ne détaille ni commissions ni swaps.
      commission: "",
      swaps: "",
      notes: "",
    };
  },
};

export const DIALECTS: Record<string, CsvDialect> = { topstepx, quantower, tradovate };

/** Dialecte correspondant aux en-têtes, ou undefined pour le format natif. */
export function detectDialect(headers: string[]): CsvDialect | undefined {
  const normalized = new Set(headers.map(normalizeHeader));
  return Object.values(DIALECTS).find((dialect) =>
    dialect.signature.every((header) => normalized.has(header)),
  );
}

/**
 * Traduit une ligne brute vers le format natif.
 *
 * Réservé aux dialectes ligne à ligne. Un dialecte fichier-entier (Quantower)
 * n'a pas de traduction ligne par ligne qui ait un sens : une exécution seule
 * ne décrit pas un trade.
 */
export function applyDialect(dialect: CsvDialect, row: RawRow): NativeRow {
  if (!dialect.map) {
    throw new Error(
      `Le dialecte ${dialect.label} se traduit au niveau du fichier, pas de la ligne`,
    );
  }
  return dialect.map(row);
}
